import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { Coffee } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

function TooltipCustom({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-zinc-800">{label}</p>
      <p className="text-zinc-600">Inicios de pausa: <strong>{d?.inicios_pausa ?? 0}</strong></p>
      <p className="text-zinc-600">Minutos en la hora: <strong>{d?.minutos_pausa ?? 0}</strong></p>
      <p className="text-amber-700">Acumulado día: <strong>{d?.minutos_acumulados ?? 0} min</strong></p>
    </div>
  );
}

export default function GraficoPausasPanel({ data }) {
  if (!data) return null;
  const serie = data.serie || [];

  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm" data-testid="grafico-pausas-panel">
      <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-medium text-amber-950">Pausas del día</h2>
            <p className="mt-1 text-xs text-amber-900/70">
              Horas con más inicios de pausa y minutos acumulados
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-amber-900">
              <Coffee className="mr-1 inline h-3.5 w-3.5" />
              {data.total_pausas ?? 0} pausas
            </span>
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-amber-900">
              {data.total_minutos ?? 0} min total
            </span>
            {data.hora_pico && (
              <span className="rounded-full bg-amber-200/80 px-2.5 py-1 font-medium text-amber-950">
                Pico: {data.hora_pico} ({data.max_inicios_hora} inicios)
              </span>
            )}
            <Button asChild size="sm" variant="outline" className="h-7 border-amber-300 bg-white/90 text-xs text-amber-900">
              <Link to="/admin/analisis-pausas">Análisis anual completo</Link>
            </Button>
          </div>
        </div>
      </div>

      {serie.every((s) => s.inicios_pausa === 0 && s.minutos_pausa === 0) ? (
        <p className="p-8 text-center text-sm text-zinc-400">Sin pausas registradas hoy en tu ámbito.</p>
      ) : (
        <div className="p-4">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={serie} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="hora" tick={{ fontSize: 11 }} interval={1} />
                <YAxis
                  yAxisId="left"
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  label={{ value: "Inicios", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  label={{ value: "Min acum.", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
                />
                <Tooltip content={<TooltipCustom />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  yAxisId="left"
                  dataKey="inicios_pausa"
                  name="Inicios de pausa"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="minutos_acumulados"
                  name="Minutos acumulados"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {data.trabajadores?.length > 0 && (
            <div className="mt-4 border-t border-zinc-100 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Más tiempo en pausa hoy</p>
              <ul className="flex flex-wrap gap-2">
                {data.trabajadores.slice(0, 8).map((t) => (
                  <li
                    key={t.usuario_id}
                    className="rounded-lg border border-zinc-100 bg-zinc-50 px-2.5 py-1.5 text-xs text-zinc-700"
                  >
                    <span className="font-medium">{t.nombre}</span>
                    <span className="text-zinc-500"> · {t.minutos} min ({t.pausas} pausas)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
